/* File: frontend/src/components/dashboard/ReputationWidget.js */
/* Version: 5.0 (Platinum Polish - Visual Consistency) */
/* Date: 2025-12-10 12:50:00 EST */
/* Description: 
 * - UI: Aligned with RecentActivityWidget (Shadows, Spacing, Fonts).
 * - UX: Added hover effects for buttons.
 * - FIX: Removed hardcoded marginTop to fix Grid alignment.
 * - COMPATIBILITY: React Router v6 Safe.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Shield, BarChart2, Play, Plus } from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    canvas:      'var(--canvas, #f8fafc)',  // класс A
    ink:         'var(--ink, #1e293b)',  // класс A
    line:        'var(--line, #e2e8f0)',  // класс A
    muted:       'var(--ink-muted, #64748b)',  // класс A
    mutedDeep:   'var(--ink-muted, #475569)',  // класс A
    surfaceSoft: 'var(--surface-soft, #f1f5f9)',  // класс A
    amber2:      'var(--amber-accent, #eab308)',  // класс B
    blue6:       'var(--blue-accent, #0369a1)',  // класс B
    chipBlue4:   'var(--chip-blue-bg, #f0f9ff)',  // класс C
};




const ReputationWidget = ({ profiles }) => {
    // Safety check
    if (!profiles || profiles.length === 0) return null;

    return (
        <div className="card" style={{
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            border: `1px solid ${P.surfaceSoft}`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden' // Ensure content fits rounded corners
        }}>
            {/* Inject Hover Styles for Buttons */}
            <style>{`
                .rep-btn { transition: all 0.2s ease; opacity: 0.9; }
                .rep-btn:hover { transform: translateY(-1px); opacity: 1; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
                .profile-item:last-child { border-bottom: none !important; }
            `}</style>

            {/* HEADER */}
            <div className="card-header" style={{
                padding: '20px 24px',
                borderBottom: `1px solid ${P.canvas}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h3 className="card-title" style={{
                    margin: 0, fontSize: '1.1rem', fontWeight: '700', color: P.ink,
                    display: 'flex', alignItems: 'center', gap: '10px'
                }}>
                    <Shield size={20} style={{ stroke: P.amber2, fill: P.amber2 }}/> Company Profiles
                </h3>
                
                <Link to="/reputation/new" className="rep-btn" style={{
                    background: P.ink, color: 'white', border: 'none',
                    borderRadius: '8px', padding: '6px 12px', textDecoration: 'none',
                    fontSize: '0.8rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px'
                }}>
                    <Plus size={14} /> Add Profile
                </Link>
            </div>
            
            {/* LIST */}
            <div className="task-list" style={{ padding: '0 24px' }}>
                {profiles.map((profile) => (
                    <div key={profile.id} className="profile-item" style={{
                        display: 'flex', 
                        flexDirection: 'column', // Mobile first logic could go here, but stick to design
                        gap: '12px',
                        padding: '20px 0',
                        borderBottom: `1px solid ${P.surfaceSoft}`
                    }}>
                        {/* Top Row: Icon + Text */}
                        <div className="task-info" style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                            {/* Avatar */}
                            <div className="task-icon" style={{
                                width: '48px', height: '48px', 
                                borderRadius: '12px', 
                                background: P.canvas, 
                                color: P.ink,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '1.1rem', fontWeight: '800',
                                border: `1px solid ${P.line}`
                            }}>
                                {profile.company_name ? profile.company_name.substring(0,2).toUpperCase() : 'CO'}
                            </div>
                            
                            {/* Text Details */}
                            <div className="task-text" style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: '700', color: P.ink }}>
                                    {profile.company_name}
                                </h4>
                                <p style={{ margin: 0, fontSize: '0.8rem', color: P.muted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    📍 {profile.address || 'No address set'}
                                </p>
                                
                                {profile.total_audits > 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                        <span style={{
                                            fontSize: '0.7rem', fontWeight: '600', 
                                            background: P.chipBlue4, color: P.blue6, 
                                            padding: '2px 8px', borderRadius: '4px'
                                        }}>
                                            {profile.total_audits} Audits run
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Bottom Row: Actions */}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                            <Link to={`/reputation/${profile.id}/analytics`} className="rep-btn" style={{
                                textDecoration: 'none', background: 'white', border: `1px solid ${P.line}`,
                                color: P.mutedDeep, borderRadius: '8px', padding: '8px 12px',
                                fontSize: '0.8rem', fontWeight: '600', display: 'flex', alignItems: 'center'
                            }}>
                                <BarChart2 size={14} style={{ marginRight: '6px' }}/> Analytics
                            </Link>
                            
                            <Link to={`/reputation/${profile.id}/audit`} className="rep-btn" style={{
                                textDecoration: 'none', background: P.amber2, border: 'none',
                                color: 'white', borderRadius: '8px', padding: '8px 12px',
                                fontSize: '0.8rem', fontWeight: '600', display: 'flex', alignItems: 'center',
                                boxShadow: '0 2px 4px rgba(234, 179, 8, 0.2)'
                            }}>
                                <Play size={14} style={{ marginRight: '6px' }} fill="white"/> Audit
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

ReputationWidget.propTypes = { profiles: PropTypes.array };
export default ReputationWidget;